import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { fetchDocumentTypes, sendDocumentReportEmail } from '../../../services/reports';
import { getCurrentUser } from '../../../services/auth';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Panel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const daysOptions = [30, 60, 90, 150];

const isValidUuid = (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);

export default function DocumentReportEmailPanel() {
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'info', msg: '' });
  const [docTypesLoading, setDocTypesLoading] = useState(true);
  const [allDocTypes, setAllDocTypes] = useState([]);

  const [form, setForm] = useState({
    organizationId: '',
    days: 30,
    recipients: '',
    summaryOnly: true,
    documentTypes: [],
    status: undefined,
  });

  // Load real user/org and document types from backend
  useEffect(() => {
    (async () => {
      try {
        setDocTypesLoading(true);
        const user = await getCurrentUser();
        const orgId = user?.organization?.id || user?.organizationId || '';
        setForm((f) => ({ ...f, organizationId: orgId }));

        const types = await fetchDocumentTypes();
        // Expect array of strings; filter out invalid/empty
        const cleaned = Array.isArray(types) ? types.filter((t) => !!t && typeof t === 'string') : [];
        setAllDocTypes(cleaned);
      } catch (e) {
        setSnack({ open: true, severity: 'error', msg: `Failed to load data: ${e?.response?.data?.message || e.message}` });
      } finally {
        setDocTypesLoading(false);
      }
    })();
  }, []);

  const errors = useMemo(() => {
    const e = {};
    if (!form.organizationId || !isValidUuid(form.organizationId)) {
      e.organizationId = 'Valid organization UUID is required';
    }
    if (!form.days || !daysOptions.includes(Number(form.days))) {
      e.days = 'Select a valid days value';
    }
    const emails = String(form.recipients || '')
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
    if (emails.length === 0) {
      e.recipients = 'At least one recipient email is required';
    } else if (!emails.every((em) => /.+@.+\..+/.test(em))) {
      e.recipients = 'All recipients must be valid emails';
    }
    if (!form.summaryOnly && (!form.documentTypes || form.documentTypes.length === 0)) {
      e.documentTypes = 'Select at least one document type when summaryOnly is disabled';
    }
    return e;
  }, [form]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (Object.keys(errors).length > 0) {
      setSnack({ open: true, severity: 'warning', msg: 'Fix validation errors before submitting' });
      return;
    }
    try {
      setLoading(true);
      const { data, correlationId } = await sendDocumentReportEmail({
        organizationId: form.organizationId,
        days: Number(form.days),
        recipients: form.recipients,
        summaryOnly: form.summaryOnly,
        documentTypes: form.documentTypes,
        status: form.status,
      });
      const jobId = data?.emailJobId || data?.jobId || 'unknown';
      setSnack({ open: true, severity: 'success', msg: `Email job queued. Job ID: ${jobId}. CID: ${correlationId}` });
    } catch (e2) {
      setSnack({ open: true, severity: 'error', msg: e2?.response?.data?.message || e2.message || 'Failed to send report email' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Typography variant="h5" sx={{ mb: 2, color: '#064d51' }}>Send Document Report Email</Typography>
      <Panel>
        <Box component="form" onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Organization ID (UUID)"
                value={form.organizationId}
                onChange={(e) => setForm((f) => ({ ...f, organizationId: e.target.value }))}
                fullWidth
                error={!!errors.organizationId}
                helperText={errors.organizationId}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Days"
                value={form.days}
                onChange={(e) => setForm((f) => ({ ...f, days: Number(e.target.value) }))}
                fullWidth
                error={!!errors.days}
                helperText={errors.days}
              >
                {daysOptions.map((d) => (
                  <MenuItem key={d} value={d}>{d} days</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Recipients (comma separated emails)"
                value={form.recipients}
                onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))}
                fullWidth
                error={!!errors.recipients}
                helperText={errors.recipients}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.summaryOnly}
                    onChange={(e) => setForm((f) => ({ ...f, summaryOnly: e.target.checked }))}
                  />
                }
                label="Send summary only"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={allDocTypes}
                loading={docTypesLoading}
                value={form.documentTypes}
                onChange={(_, val) => setForm((f) => ({ ...f, documentTypes: val }))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Document Types"
                    placeholder={docTypesLoading ? 'Loading...' : 'Select document types'}
                    error={!!errors.documentTypes}
                    helperText={errors.documentTypes}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: '#064d51' }}>
                  {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Send Email'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Panel>

      <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
}
